/**
 * Reset Password Page Component
 * Allows users to set a new password using a reset token from email
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, Alert, Typography, Tooltip, Result, Spin } from 'antd';
import { LockOutlined, InfoCircleOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { resetPassword, validateResetToken } from '../../services/authService';
import PagesHeader from '../../PagesHeader';
import styles from './ResetPassword.module.css';

const { Title, Text } = Typography;

/**
 * Password requirements for validation
 */
const PASSWORD_REQUIREMENTS = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireDigit: true,
  requireSpecialChar: true,
};

/**
 * Reset Password page - set new password with token from email
 */
const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);

  // Get token and email from URL params
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  // Validate URL params and token on mount
  useEffect(() => {
    const validateToken = async () => {
      // Check if URL params are present
      if (!token || !email) {
        setInvalidLink(true);
        setValidatingToken(false);
        return;
      }

      try {
        // Validate the token with the backend
        const response = await validateResetToken(email, token);
        if (!response.isValid) {
          setInvalidLink(true);
        }
      } catch (err) {
        // If validation fails, still show the form and let submit handle errors
        // This is a fallback in case the validation endpoint has issues
        console.warn('Token validation failed, proceeding with form display');
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token, email]);

  /**
   * Validate password meets requirements
   */
  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject(new Error(t('auth.passwordRequired')));
    }
    if (value.length < PASSWORD_REQUIREMENTS.minLength) {
      return Promise.reject(new Error(t('auth.passwordTooShort', { min: PASSWORD_REQUIREMENTS.minLength })));
    }
    if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(value)) {
      return Promise.reject(new Error(t('auth.passwordNeedsUppercase')));
    }
    if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(value)) {
      return Promise.reject(new Error(t('auth.passwordNeedsLowercase')));
    }
    if (PASSWORD_REQUIREMENTS.requireDigit && !/[0-9]/.test(value)) {
      return Promise.reject(new Error(t('auth.passwordNeedsDigit')));
    }
    if (PASSWORD_REQUIREMENTS.requireSpecialChar && !/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
      return Promise.reject(new Error(t('auth.passwordNeedsSpecial')));
    }
    return Promise.resolve();
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (values) => {
    setSubmitting(true);
    setError(null);

    try {
      await resetPassword(email, token, values.newPassword, values.confirmPassword);
      setResetSuccess(true);
    } catch (err) {
      if (err.message?.includes('expired') || err.message?.includes('invalid')) {
        setError(t('auth.resetLinkExpired'));
      } else {
        setError(err.message || t('auth.resetPasswordFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading state while validating token
  if (validatingToken) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <main className={styles.main}>
          <div className={styles.card}>
            <div className={styles.loadingContainer}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
              <Text className={styles.loadingText}>
                {t('auth.validatingLink') || 'Validating reset link...'}
              </Text>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show invalid link message
  if (invalidLink) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <main className={styles.main}>
          <div className={styles.card}>
            <Result
              status="error"
              title={t('auth.invalidResetLink')}
              subTitle={t('auth.invalidResetLinkMessage')}
              extra={[
                <Link to="/forgot-password" key="forgot">
                  <Button type="primary" className={styles.submitButton}>
                    {t('auth.requestNewLink')}
                  </Button>
                </Link>,
                <Link to="/login" key="login">
                  <Button>{t('auth.backToLogin')}</Button>
                </Link>,
              ]}
            />
          </div>
        </main>
      </div>
    );
  }

  // Show success message after password reset
  if (resetSuccess) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <main className={styles.main}>
          <div className={styles.card}>
            <div className={styles.successContainer}>
              <CheckCircleOutlined className={styles.successIcon} />
              <Title level={3} className={styles.successTitle}>
                {t('auth.passwordResetSuccess')}
              </Title>
              <Text className={styles.successText}>
                {t('auth.passwordResetSuccessMessage')}
              </Text>
              <Link to="/login">
                <Button type="primary" size="large" className={styles.submitButton}>
                  {t('auth.signIn')}
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <PagesHeader />
      
      <main className={styles.main}>
        <div className={styles.card}>
          <div className={styles.header}>
            <Title level={2} className={styles.title}>
              {t('auth.resetPasswordTitle')}
            </Title>
            <Text type="secondary" className={styles.subtitle}>
              {t('auth.resetPasswordSubtitle')}
            </Text>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              className={styles.alert}
            />
          )}

          <Form
            form={form}
            name="resetPassword"
            onFinish={handleSubmit}
            layout="vertical"
            requiredMark={false}
            className={styles.form}
          >
            <Form.Item
              name="newPassword"
              label={
                <span>
                  {t('auth.newPassword')}
                  <Tooltip title={t('auth.passwordRequirements')}>
                    <InfoCircleOutlined className={styles.infoIcon} />
                  </Tooltip>
                </span>
              }
              rules={[{ validator: validatePassword }]}
            >
              <Input.Password
                prefix={<LockOutlined className={styles.inputIcon} />}
                placeholder={t('auth.newPasswordPlaceholder')}
                size="large"
                autoComplete="new-password"
                disabled={submitting}
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={t('auth.confirmNewPassword')}
              dependencies={['newPassword']}
              rules={[
                { required: true, message: t('auth.confirmPasswordRequired') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('auth.passwordsMustMatch')));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className={styles.inputIcon} />}
                placeholder={t('auth.confirmNewPasswordPlaceholder')}
                size="large"
                autoComplete="new-password"
                disabled={submitting}
              />
            </Form.Item>

            <div className={styles.passwordHint}>
              <Text type="secondary" className={styles.hintText}>
                {t('auth.passwordRequirements')}
              </Text>
            </div>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
                className={styles.submitButton}
              >
                {submitting ? t('auth.resettingPassword') : t('auth.resetPassword')}
              </Button>
            </Form.Item>
          </Form>

          <div className={styles.helpText}>
            <Text type="secondary">
              {t('auth.rememberPassword')}{' '}
              <Link to="/login" className={styles.link}>
                {t('auth.signIn')}
              </Link>
            </Text>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
